import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Coins, Star, Clover } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { sfx, resumeAudio } from "../lib/sound";
import { SoundToggle } from "../components/SoundToggle";

const START = { argent: 0, gloire: 0, energie: 100, chance: 50 };
const FEEDBACK_MS = 1300;
const TOTAL_TIME = 60;

function applyEffets(s, e, luckBonus = 0) {
  const ns = {
    argent: Math.max(0, s.argent + (e.argent || 0)),
    gloire: Math.max(0, s.gloire + (e.gloire || 0)),
    energie: Math.max(0, Math.min(100, s.energie + (e.energie || 0))),
    chance: Math.max(0, Math.min(100, s.chance + (e.chance || 0) + luckBonus)),
  };
  return ns;
}

const StatChip = ({ icon: Icon, value, testid, color }) => (
  <div
    data-testid={testid}
    className="flex flex-1 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.06] py-2 backdrop-blur-md"
  >
    <Icon size={16} style={{ color }} />
    <motion.span
      key={value}
      initial={{ scale: 1.35 }}
      animate={{ scale: 1 }}
      className="mt-1 font-display text-sm font-bold text-white"
    >
      {value.toLocaleString()}
    </motion.span>
  </div>
);

export const GameScreen = ({ domain, pseudo, playerId, luckBonus = 0, challengeOf, onEnd }) => {
  const { t, lang, dict } = useLang();
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [stats, setStats] = useState({ ...START, chance: START.chance + luckBonus });
  const [time, setTime] = useState(TOTAL_TIME);
  const [feedback, setFeedback] = useState(null);
  const [locked, setLocked] = useState(false);
  const [phase, setPhase] = useState("loading"); // loading | playing | submitting
  const [shake, setShake] = useState(false);

  const statsRef = useRef(stats);
  const choicesRef = useRef([]);
  const endedRef = useRef(false);
  const startRef = useRef(Date.now());
  const timerRef = useRef(null);

  const finish = useCallback(
    async (reason) => {
      if (endedRef.current) return;
      endedRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      setPhase("submitting");
      const duree = Math.min(90, Math.max(1, Math.round((Date.now() - startRef.current) / 1000)));
      try {
        const res = await api.saveGame({
          playerId,
          pseudo,
          domaine: domain.id,
          choices: choicesRef.current,
          duree,
          challengeOf: challengeOf || null,
        });
        onEnd(res, reason);
      } catch (e) {
        // Repli: résultat local si l'API échoue
        onEnd(
          {
            ...statsRef.current,
            pseudo,
            domaine: domain.id,
            score: 0,
            rank: "anonyme",
            rankPosition: null,
            percentile: null,
          },
          reason
        );
      }
    },
    [domain, pseudo, playerId, challengeOf, onEnd]
  );

  // Charger la session
  useEffect(() => {
    let active = true;
    api
      .getSession(domain.id, lang)
      .then((data) => {
        if (!active) return;
        setEvents(data.events);
        setPhase("playing");
        startRef.current = Date.now();
        resumeAudio();
      })
      .catch(() => active && finish("fin"));
    return () => {
      active = false;
    };
  }, [domain, lang, finish]);

  // Chrono
  useEffect(() => {
    if (phase !== "playing") return;
    timerRef.current = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          finish("temps");
          return 0;
        }
        if (prev <= 11) sfx.tick();
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, finish]);

  const choose = (choiceIndex) => {
    if (locked || endedRef.current || phase !== "playing") return;
    const ev = events[idx];
    const opt = ev.choix[choiceIndex];
    let failed = false;
    let effets = opt.effets;
    let text = opt.feedback;

    if (opt.risque) {
      const success = Math.random() * 100 < statsRef.current.chance;
      if (!success) {
        failed = true;
        effets = { gloire: -10, energie: -15 };
        text = `${t("riskFail")} ${opt.feedback}`;
        sfx.fail();
        setShake(true);
        setTimeout(() => setShake(false), 500);
      } else {
        text = `${t("riskSuccess")} ${opt.feedback}`;
        sfx.success();
      }
    } else {
      sfx.choose();
    }

    choicesRef.current.push({ eventId: ev.id, choiceIndex, failed });
    const ns = applyEffets(statsRef.current, effets);
    statsRef.current = ns;
    setStats(ns);
    setFeedback({ text, negative: failed || (effets.energie || 0) < -20 });
    setLocked(true);

    if (ns.energie <= 0) {
      setTimeout(() => finish("energie"), FEEDBACK_MS);
      return;
    }
    if (idx + 1 >= events.length) {
      setTimeout(() => finish("fin"), FEEDBACK_MS);
      return;
    }
    setTimeout(() => {
      setIdx((i) => i + 1);
      setFeedback(null);
      setLocked(false);
    }, FEEDBACK_MS);
  };

  if (phase === "loading" || events.length === 0) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="animate-pulse-glow font-display text-lg text-white/60">{t("loading")}</p>
      </div>
    );
  }

  if (phase === "submitting") {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="h-12 w-12 rounded-full border-4 border-white/10 border-t-[#f093fb]"
        />
        <p className="font-display text-lg text-white/70">{t("computingDestiny")}</p>
      </div>
    );
  }

  const ev = events[idx];
  const energyLow = stats.energie < 25;
  const progress = ((idx + 1) / events.length) * 100;

  return (
    <div className={`flex min-h-full flex-col px-5 py-6 ${shake ? "animate-shake" : ""}`} data-testid="game-screen">
      {/* Timer */}
      <div className="mb-3 flex items-center justify-between">
        <div
          data-testid="game-timer"
          className={`flex items-center gap-2 font-display text-2xl font-black ${
            time <= 10 ? "text-[#f5576c]" : "text-white"
          }`}
        >
          <Clock size={22} className={time <= 10 ? "animate-pulse-glow" : ""} /> {time}s
        </div>
        <span className="flex items-center gap-3">
          <span className="font-display text-xs uppercase tracking-widest text-white/40">
            {idx + 1}/{events.length}
          </span>
          <SoundToggle />
        </span>
      </div>

      {/* Stats */}
      <div className="flex gap-2">
        <StatChip icon={Coins} value={stats.argent} testid="stat-argent" color="#fbbf24" />
        <StatChip icon={Star} value={stats.gloire} testid="stat-gloire" color="#f093fb" />
        <StatChip icon={Clover} value={stats.chance} testid="stat-chance" color="#38ef7d" />
      </div>

      {/* Barre d'énergie */}
      <div className="mt-3">
        <div className="mb-1 flex justify-between text-[11px] font-semibold uppercase tracking-widest text-white/40">
          <span>{t("energie")}</span>
          <span>{stats.energie}</span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/10">
          <motion.div
            data-testid="energy-fill"
            animate={{ width: `${stats.energie}%` }}
            transition={{ duration: 0.4 }}
            className={`h-full rounded-full ${energyLow ? "animate-pulse-glow" : ""}`}
            style={{
              background: energyLow
                ? "linear-gradient(90deg,#f5576c,#eb3349)"
                : "linear-gradient(90deg,#38ef7d,#11998e)",
              boxShadow: `0 0 12px ${energyLow ? "rgba(245,87,108,0.7)" : "rgba(56,239,125,0.6)"}`,
            }}
          />
        </div>
      </div>

      {/* Progression */}
      <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-white/5">
        <motion.div animate={{ width: `${progress}%` }} className="h-full bg-[#f093fb]/60" />
      </div>

      {/* Événement */}
      <div className="flex flex-1 flex-col justify-center py-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`rounded-3xl border p-6 backdrop-blur-md transition-colors ${
              ev.rare
                ? "border-[#ffd700]/60 bg-gradient-to-b from-[#ffd700]/10 to-white/[0.04] shadow-[0_0_40px_-8px_rgba(255,215,0,0.4)]"
                : "border-white/10 bg-white/[0.06]"
            }`}
          >
            {ev.rare ? (
              <span className="mb-3 inline-block rounded-full bg-gradient-to-r from-[#ffd700] to-[#f5576c] px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-black">
                {ev.rarete === "legendaire" ? "🌟 Légendaire" : "⚡ Épique"}
              </span>
            ) : ev.climax && (
              <span className="mb-3 inline-block rounded-full bg-gradient-to-r from-[#f093fb] to-[#f5576c] px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-white">
                ⚡ Climax
              </span>
            )}
            <p data-testid="event-text" className="text-lg font-medium leading-relaxed text-white">
              {ev.texte}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-4 min-h-[24px] text-center">
          <AnimatePresence>
            {feedback && (
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                data-testid="event-feedback"
                className={`text-sm italic ${feedback.negative ? "text-[#f5576c]" : "text-[#f093fb]"}`}
              >
                {feedback.text}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Choix */}
      <div className="grid gap-3">
        {ev.choix.map((c, i) => (
          <motion.button
            key={i}
            whileTap={{ scale: 0.96 }}
            disabled={locked}
            onClick={() => choose(i)}
            data-testid={`choice-button-${i}`}
            className="rounded-2xl py-4 px-5 text-center font-display text-base font-bold text-white shadow-lg transition-opacity disabled:opacity-60"
            style={{
              background:
                i === 0
                  ? "linear-gradient(90deg,#11998e,#38ef7d)"
                  : "linear-gradient(90deg,#eb3349,#f45c43)",
            }}
          >
            {c.label}
            {c.risque && <span className="ml-2 text-xs opacity-80">🎲</span>}
          </motion.button>
        ))}
      </div>
    </div>
  );
};
